/**
 * Shared Sound Player for all games
 * Uses Howler.js for reliable cross-platform audio playback,
 * especially optimized for iOS Safari
 */

import { Howl, Howler } from 'howler';

// Import shared sound files
import victorySound from '../Assets/Sounds/sfx-victory.mp3';
import defeatSound from '../Assets/Sounds/sfx-defeat.mp3';
import hintSound from '../Assets/Sounds/sfx-hint.mp3';
import timeCountdownSound from '../Assets/Sounds/sfx-time-countdown.mp3';
import menuClickSound from '../Assets/Sounds/sfx-menu-click.mp3';

/**
 * Shared sound effects available across all games
 */
export enum SharedSoundEffect {
  VICTORY = 'victory',
  DEFEAT = 'defeat',
  HINT = 'hint',
  TIME_COUNTDOWN = 'time-countdown',
  MENU_CLICK = 'menu-click'
}

// Map shared sound effects to their file paths
const sharedSoundMap: Record<SharedSoundEffect, string> = {
  [SharedSoundEffect.VICTORY]: victorySound,
  [SharedSoundEffect.DEFEAT]: defeatSound,
  [SharedSoundEffect.HINT]: hintSound,
  [SharedSoundEffect.TIME_COUNTDOWN]: timeCountdownSound,
  [SharedSoundEffect.MENU_CLICK]: menuClickSound
};

// Howl instances cache - Howler.js handles sprite loading, pooling, and iOS quirks automatically
const howlCache: Map<string, Howl> = new Map();

/**
 * Preload shared sound effects immediately
 * Howler.js handles all the complexity of mobile audio
 */
export function preloadSharedSounds(): void {
  Object.entries(sharedSoundMap).forEach(([key, path]) => {
    if (!howlCache.has(path)) {
      const howl = new Howl({
        src: [path],
        preload: true,
        html5: true, // Use HTML5 Audio for iOS compatibility - Web Audio API can have issues
        volume: 0.5,
      });
      howlCache.set(path, howl);
    }
  });
}

// Preload all shared sounds immediately when module loads
preloadSharedSounds();

/**
 * Play a sound effect using Howler.js for reliable mobile playback
 * @param soundPath - Either a SharedSoundEffect enum value or a custom MP3 file path
 * @param volume - Volume level (0-1), defaults to 0.5
 */
export function playSound(soundPath: SharedSoundEffect | string, volume: number = 0.5): void {
  try {
    // If it's a SharedSoundEffect, get the actual file path
    const actualPath = (soundPath in sharedSoundMap) 
      ? sharedSoundMap[soundPath as SharedSoundEffect]
      : soundPath;
    
    let howl = howlCache.get(actualPath);
    
    // If not cached, create and cache it
    if (!howl) {
      howl = new Howl({
        src: [actualPath],
        preload: true,
        html5: true, // Use HTML5 Audio for iOS compatibility
        volume: Math.max(0, Math.min(1, volume)),
      });
      howlCache.set(actualPath, howl);
    }
    
    // Set volume and play
    // Howler.js handles sprite pooling automatically - multiple calls work perfectly
    howl.volume(Math.max(0, Math.min(1, volume)));
    howl.play();
  } catch (error) {
    console.warn(`Error playing sound effect ${soundPath}:`, error);
  }
}

/**
 * Stop all currently playing sounds
 */
export function stopAllSounds(): void {
  howlCache.forEach(howl => {
    howl.stop();
  });
}

/**
 * Stop a specific sound
 * @param soundPath - Either a SharedSoundEffect enum value or a custom MP3 file path
 */
export function stopSound(soundPath: SharedSoundEffect | string): void {
  const actualPath = (soundPath in sharedSoundMap) 
    ? sharedSoundMap[soundPath as SharedSoundEffect]
    : soundPath;
  
  const howl = howlCache.get(actualPath);
  if (howl) {
    howl.stop();
  }
}
